import React from 'react';
import type { NotificationDropdownProps } from './NotificationDropdown.types';
import { useNotificationDropdown } from './NotificationDropdown.hook';
import { NotificationDetailModal } from './NotificationDetailModal';
import { getTypeIcon, formatRelativeTime } from './NotificationDropdown.utils';
import * as styles from './NotificationDropdown.styles';

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onUnreadCountChange,
}) => {
  const {
    notifications,
    unreadCount,
    activeTab,
    setActiveTab,
    loading,
    fetchingMore,
    listRef,
    handleScroll,
    markAsRead,
    markAllAsRead,
    selectedNotification,
    setSelectedNotification,
  } = useNotificationDropdown(isOpen, onUnreadCountChange);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay to close when clicking outside */}
      <div 
        className="fixed inset-0 z-40 bg-transparent" 
        onClick={onClose} 
        data-testid="notification-dropdown-backdrop"
      />

      {/* Main Glassmorphic Dropdown Panel */}
      <div className={styles.dropdownContainer} data-testid="notification-dropdown-panel">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <i className="fa-solid fa-bell text-emerald-400"></i>
            <span>Notifications</span>
            {unreadCount > 0 && (
              <span className={styles.unreadBadge} data-testid="unread-count-badge">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className={styles.markAllBtn}
            data-testid="mark-all-read-btn"
          >
            <i className="fa-solid fa-check-double"></i>
            <span>Mark all as read</span>
          </button>
        </div>

        {/* Tab Filters */}
        <div className={styles.tabContainer}>
          <button
            onClick={() => setActiveTab('all')}
            className={styles.getTabClass(activeTab === 'all')}
            data-testid="tab-all"
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={styles.getTabClass(activeTab === 'unread')}
            data-testid="tab-unread"
          >
            Unread ({unreadCount})
          </button>
        </div>

        {/* Scrollable List Container (Infinite Scroll) */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className={styles.listContainer}
          data-testid="notification-list-container"
        >
          {loading && notifications.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
              <i className="fa-solid fa-circle-notch animate-spin text-emerald-400 text-sm"></i>
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.emptyState} data-testid="notification-empty-state">
              <div className="w-12 h-12 rounded-full bg-slate-800/80 flex items-center justify-center mb-2 text-slate-500 text-xl">
                <i className="fa-solid fa-bell-slash"></i>
              </div>
              <p className="text-xs font-semibold text-slate-300">No notifications</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {activeTab === 'unread' ? 'You have read all notifications' : 'New notifications will appear here'}
              </p>
            </div>
          ) : (
            notifications.map((item) => (
              <div
                key={item.id}
                onClick={() => {
                  if (!item.read) markAsRead(item.id);
                  setSelectedNotification(item);
                }}
                className={styles.getItemClass(item.read)}
                data-testid={`notification-item-${item.id}`}
              >
                <div className="text-base pt-0.5 shrink-0">
                  <i className={getTypeIcon(item.type)}></i>
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <p className={styles.itemTitle}>{item.message}</p>
                  <p className={styles.itemTime}>
                    <span>{formatRelativeTime(item.created_at)}</span>
                  </p>
                </div>

                {/* Quick Action / Status indicator */}
                <div className="absolute right-3 top-3.5 flex items-center gap-1">
                  {!item.read ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(item.id);
                      }}
                      className="text-slate-500 hover:text-emerald-400 p-1 transition-colors"
                      title="Mark as read"
                      data-testid={`mark-read-btn-${item.id}`}
                    >
                      <i className="fa-solid fa-check text-xs"></i>
                    </button>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-700"></span>
                  )}
                </div>
              </div>
            ))
          )}

          {fetchingMore && (
            <div className="p-3 text-center text-xs text-slate-400 flex items-center justify-center gap-2 bg-slate-950/40">
              <i className="fa-solid fa-circle-notch animate-spin text-emerald-400 text-xs"></i>
              <span>Loading older notifications...</span>
            </div>
          )}
        </div>
      </div>

      {/* Notification Detail View Modal */}
      <NotificationDetailModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onMarkAsRead={markAsRead}
      />
    </>
  );
};
